// src/pages/creator-panel/CRM/ActivityComm/WorkflowDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, CircularProgress,
  Alert, Stack, Stepper, Step, StepLabel, StepContent, Collapse,
  Tooltip, Pagination, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  PlayCircle as PlayCircleIcon,
  ThumbUp as ThumbUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Assignment as AssignmentIcon,
  Event as EventIcon,
  Notifications as NotificationsIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Rule as RuleIcon,
  ThumbUpAlt as ThumbUpAltIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { workflowService } from './activityCommService';
import {
  WorkflowTypeLabels, WorkflowTypeColors,
  WorkflowTriggerTypeLabels, WorkflowStepTypeLabels,
  WorkflowExecutionStatus, WorkflowExecutionStatusLabels,
  WorkflowExecutionStatusColors, StepExecutionStatusLabels,
  StepExecutionStatusColors, formatDateTime
} from './activityCommEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const STEP_TYPE_ICONS = {
  SEND_EMAIL: EmailIcon,
  SEND_SMS: SmsIcon,
  CREATE_TASK: AssignmentIcon,
  SCHEDULE_APPOINTMENT: EventIcon,
  SEND_NOTIFICATION: NotificationsIcon,
  WAIT_DELAY: HourglassEmptyIcon,
  CHECK_CONDITION: RuleIcon,
  REQUIRE_APPROVAL: ThumbUpAltIcon
};

const getStepConfigSummary = (step) => {
  if (!step) return '';
  const config = step.config || step.step_config || {};
  switch (step.step_type) {
    case 'SEND_EMAIL':
      return config.subject ? `Email: ${config.subject}` : 'Send Email';
    case 'SEND_SMS':
      return config.message
        ? `SMS: ${config.message.length > 60 ? config.message.slice(0, 60) + '...' : config.message}`
        : 'Send SMS';
    case 'CREATE_TASK':
      return config.title ? `Task: ${config.title}` : 'Create Task';
    case 'SCHEDULE_APPOINTMENT':
      return config.title ? `Appointment: ${config.title}` : 'Schedule Appointment';
    case 'SEND_NOTIFICATION':
      return config.message
        ? `Notification: ${config.message.length > 60 ? config.message.slice(0, 60) + '...' : config.message}`
        : 'Send Notification';
    case 'WAIT_DELAY':
      if (config.days || config.hours || config.minutes) {
        const parts = [];
        if (config.days) parts.push(`${config.days} day${config.days > 1 ? 's' : ''}`);
        if (config.hours) parts.push(`${config.hours} hour${config.hours > 1 ? 's' : ''}`);
        if (config.minutes) parts.push(`${config.minutes} min`);
        return `Wait: ${parts.join(', ')}`;
      }
      return config.delay_minutes ? `Wait: ${config.delay_minutes} min` : 'Wait / Delay';
    case 'CHECK_CONDITION':
      return config.condition ? `Condition: ${config.condition}` : 'Check Condition';
    case 'REQUIRE_APPROVAL':
      return config.approver ? `Approval by: ${config.approver}` : 'Require Approval';
    default:
      return WorkflowStepTypeLabels[step.step_type] || step.step_type || '';
  }
};

const getTriggerConfigSummary = (workflow) => {
  if (!workflow) return '';
  const config = workflow.trigger_config || {};
  switch (workflow.trigger_type) {
    case 'AUTO_AFTER_SERVICE':
      return config.delay_days ? `${config.delay_days} days after service` : 'After service completed';
    case 'AUTO_AFTER_PURCHASE':
      return config.delay_days ? `${config.delay_days} days after purchase` : 'After purchase';
    case 'AUTO_OVERDUE_PAYMENT':
      return config.delay_days ? `${config.delay_days} days overdue` : 'Overdue payment';
    case 'SCHEDULED':
      return config.cron ? `Schedule: ${config.cron}` : (config.schedule || 'Scheduled');
    case 'MANUAL':
      return 'Triggered manually';
    default:
      return '';
  }
};

const EXECUTIONS_PER_PAGE = 10;

const WorkflowDetail = () => {
  const { chatbotId, workflowId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [workflow, setWorkflow] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [executionsPagination, setExecutionsPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggerContactIds, setTriggerContactIds] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const loadWorkflow = useCallback(async () => {
    setLoading(true);
    try {
      const response = await workflowService.getById(workflowId);
      if (response.success) {
        setWorkflow(response.data);
        document.title = `${response.data.name} | Workflow Detail`;
      } else {
        enqueueSnackbar('Failed to load workflow', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to load workflow', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [workflowId, enqueueSnackbar]);

  const loadExecutions = useCallback(async (page = 1) => {
    setExecutionsLoading(true);
    try {
      const response = await workflowService.getExecutions(workflowId, {
        page,
        per_page: EXECUTIONS_PER_PAGE
      });
      if (response.success) {
        setExecutions(response.data?.executions || response.data || []);
        setExecutionsPagination({
          page: response.data?.page || page,
          total_pages: response.data?.total_pages || 1,
          total: response.data?.total || 0
        });
      }
    } catch (err) {
      console.error('Failed to load executions:', err);
    } finally {
      setExecutionsLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    document.title = 'Workflow Detail | Activity';
    if (workflowId) {
      loadWorkflow();
      loadExecutions(1);
    }
  }, [workflowId, loadWorkflow, loadExecutions]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const contactIds = triggerContactIds
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));
      const response = await workflowService.trigger(workflowId, { contact_ids: contactIds });
      if (response.success) {
        enqueueSnackbar('Workflow triggered successfully', { variant: 'success' });
        setTriggerOpen(false);
        setTriggerContactIds('');
        loadExecutions(1);
      } else {
        enqueueSnackbar(response.message || 'Failed to trigger workflow', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to trigger workflow', { variant: 'error' });
    } finally {
      setTriggering(false);
    }
  };

  const handleExecutionAction = async (executionId, action) => {
    setActionLoading(prev => ({ ...prev, [executionId]: action }));
    try {
      let response;
      let successMsg;
      switch (action) {
        case 'cancel':
          response = await workflowService.cancelExecution(executionId);
          successMsg = 'Execution cancelled';
          break;
        case 'pause':
          response = await workflowService.pauseExecution(executionId);
          successMsg = 'Execution paused';
          break;
        case 'resume':
          response = await workflowService.resumeExecution(executionId);
          successMsg = 'Execution resumed';
          break;
        case 'approve':
          response = await workflowService.approveStep(executionId);
          successMsg = 'Step approved';
          break;
        default:
          return;
      }
      if (response.success) {
        enqueueSnackbar(successMsg, { variant: 'success' });
        loadExecutions(executionsPagination.page);
      } else {
        enqueueSnackbar(response.message || `Failed to ${action} execution`, { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(`Failed to ${action} execution`, { variant: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [executionId]: null }));
    }
  };

  const toggleExpandRow = (executionId) => {
    setExpandedRows(prev => ({ ...prev, [executionId]: !prev[executionId] }));
  };

  const handlePageChange = (event, page) => {
    loadExecutions(page);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        Workflow not found
      </Alert>
    );
  }

  const steps = workflow.steps || [];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 }, fontFamily }}>
      {/* Top Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ fontFamily, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {workflow.name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/workflows/${workflowId}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={() => setTriggerOpen(true)}
          >
            Trigger
          </Button>
        </Stack>
      </Box>

      <Stack spacing={3}>
        {/* Workflow Info Card */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily, mb: 2 }}>
            Workflow Info
          </Typography>
          <Stack spacing={2}>
            {workflow.description && (
              <Box>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {workflow.description}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <Chip
                label={WorkflowTypeLabels[workflow.workflow_type] || workflow.workflow_type}
                color={WorkflowTypeColors[workflow.workflow_type] || 'default'}
                size="small"
              />
              <Chip
                label={WorkflowTriggerTypeLabels[workflow.trigger_type] || workflow.trigger_type}
                size="small"
                variant="outlined"
              />
              <Chip
                label={workflow.is_active ? 'Active' : 'Inactive'}
                color={workflow.is_active ? 'success' : 'default'}
                size="small"
              />
            </Box>
            {getTriggerConfigSummary(workflow) && (
              <Box>
                <Typography variant="caption" color="text.secondary">Trigger Config</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {getTriggerConfigSummary(workflow)}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Steps</Typography>
                <Typography variant="body2">{steps.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{formatDateTime(workflow.created_at)}</Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>

        {/* Workflow Steps */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily, mb: 2 }}>
            Workflow Steps
          </Typography>
          {steps.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No steps configured</Typography>
          ) : (
            <Stepper orientation="vertical" activeStep={-1} sx={{ '& .MuiStepConnector-line': { minHeight: 20 } }}>
              {steps
                .sort((a, b) => (a.step_order || a.order || 0) - (b.step_order || b.order || 0))
                .map((step, index) => {
                  const StepIcon = STEP_TYPE_ICONS[step.step_type] || AssignmentIcon;
                  return (
                    <Step key={step.id || index} active>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '50%',
                            bgcolor: 'primary.main', display: 'flex',
                            alignItems: 'center', justifyContent: 'center'
                          }}>
                            <StepIcon sx={{ color: '#fff', fontSize: 18 }} />
                          </Box>
                        )}
                      >
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontFamily }}>
                          {step.name || WorkflowStepTypeLabels[step.step_type] || `Step ${index + 1}`}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary">
                          {getStepConfigSummary(step)}
                        </Typography>
                      </StepContent>
                    </Step>
                  );
                })}
            </Stepper>
          )}
        </Paper>

        {/* Execution History */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily, mb: 2 }}>
            Execution History
          </Typography>
          {executionsLoading && executions.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : executions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No executions yet</Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily, fontWeight: 600 }} />
                      <TableCell sx={{ fontFamily, fontWeight: 600 }}>Contact</TableCell>
                      <TableCell sx={{ fontFamily, fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontFamily, fontWeight: 600 }}>Current Step</TableCell>
                      <TableCell sx={{ fontFamily, fontWeight: 600 }}>Started</TableCell>
                      <TableCell sx={{ fontFamily, fontWeight: 600 }}>Completed</TableCell>
                      <TableCell sx={{ fontFamily, fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {executions.map((execution) => {
                      const isExpanded = !!expandedRows[execution.id];
                      const canCancel = [
                        WorkflowExecutionStatus.RUNNING,
                        WorkflowExecutionStatus.WAITING_DELAY,
                        WorkflowExecutionStatus.WAITING_APPROVAL
                      ].includes(execution.status);
                      const canPause = execution.status === WorkflowExecutionStatus.RUNNING;
                      const canResume = execution.status === WorkflowExecutionStatus.PAUSED;
                      const canApprove = execution.status === WorkflowExecutionStatus.WAITING_APPROVAL;

                      return (
                        <React.Fragment key={execution.id}>
                          <TableRow hover sx={{ '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}>
                            <TableCell sx={{ width: 40 }}>
                              <IconButton size="small" onClick={() => toggleExpandRow(execution.id)}>
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontFamily }}>
                              {execution.contact_name || execution.contact?.full_name || execution.contact?.name || `Contact #${execution.contact_id || '-'}`}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={WorkflowExecutionStatusLabels[execution.status] || execution.status}
                                color={WorkflowExecutionStatusColors[execution.status] || 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ fontFamily }}>
                              {execution.current_step_name || execution.current_step || '-'}
                            </TableCell>
                            <TableCell sx={{ fontFamily }}>
                              {formatDateTime(execution.started_at || execution.created_at)}
                            </TableCell>
                            <TableCell sx={{ fontFamily }}>
                              {formatDateTime(execution.completed_at)}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5}>
                                {canCancel && (
                                  <Tooltip title="Cancel">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleExecutionAction(execution.id, 'cancel')}
                                      disabled={!!actionLoading[execution.id]}
                                    >
                                      {actionLoading[execution.id] === 'cancel' ? <CircularProgress size={16} /> : <CancelIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canPause && (
                                  <Tooltip title="Pause">
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      onClick={() => handleExecutionAction(execution.id, 'pause')}
                                      disabled={!!actionLoading[execution.id]}
                                    >
                                      {actionLoading[execution.id] === 'pause' ? <CircularProgress size={16} /> : <PauseIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canResume && (
                                  <Tooltip title="Resume">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleExecutionAction(execution.id, 'resume')}
                                      disabled={!!actionLoading[execution.id]}
                                    >
                                      {actionLoading[execution.id] === 'resume' ? <CircularProgress size={16} /> : <PlayCircleIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canApprove && (
                                  <Tooltip title="Approve">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleExecutionAction(execution.id, 'approve')}
                                      disabled={!!actionLoading[execution.id]}
                                    >
                                      {actionLoading[execution.id] === 'approve' ? <CircularProgress size={16} /> : <ThumbUpIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {/* Expanded Step Execution Details */}
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0, px: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 4, py: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontFamily, mb: 1 }}>
                                    Step Execution Log
                                  </Typography>
                                  {(!execution.step_executions || execution.step_executions.length === 0) ? (
                                    <Typography variant="body2" color="text.secondary">No step execution data available</Typography>
                                  ) : (
                                    <Stack spacing={1}>
                                      {execution.step_executions.map((stepExec, idx) => {
                                        const StepExecIcon = STEP_TYPE_ICONS[stepExec.step_type] || AssignmentIcon;
                                        return (
                                          <Paper key={stepExec.id || idx} variant="outlined" sx={{ p: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                              <StepExecIcon fontSize="small" color="action" />
                                              <Typography variant="body2" fontWeight={600} sx={{ fontFamily }}>
                                                {stepExec.step_name || WorkflowStepTypeLabels[stepExec.step_type] || `Step ${idx + 1}`}
                                              </Typography>
                                              <Chip
                                                label={StepExecutionStatusLabels[stepExec.status] || stepExec.status}
                                                color={StepExecutionStatusColors[stepExec.status] || 'default'}
                                                size="small"
                                              />
                                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                                {formatDateTime(stepExec.started_at)} {stepExec.completed_at ? ` - ${formatDateTime(stepExec.completed_at)}` : ''}
                                              </Typography>
                                            </Box>
                                            {stepExec.output && (
                                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 3.5 }}>
                                                {typeof stepExec.output === 'string' ? stepExec.output : JSON.stringify(stepExec.output)}
                                              </Typography>
                                            )}
                                            {stepExec.error && (
                                              <Typography variant="body2" color="error.main" sx={{ mt: 0.5, pl: 3.5 }}>
                                                Error: {stepExec.error}
                                              </Typography>
                                            )}
                                          </Paper>
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {executionsPagination.total_pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={executionsPagination.total_pages}
                    page={executionsPagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      </Stack>

      {/* Trigger Dialog */}
      <Dialog open={triggerOpen} onClose={() => setTriggerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>Trigger Workflow</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter comma-separated contact IDs to trigger this workflow for.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Contact IDs"
            placeholder="e.g. 1, 2, 3"
            value={triggerContactIds}
            onChange={(e) => setTriggerContactIds(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerOpen(false)} disabled={triggering}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTrigger}
            disabled={triggering || !triggerContactIds.trim()}
            startIcon={triggering ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          >
            {triggering ? 'Triggering...' : 'Trigger'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowDetail;
